import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchPacingHolds,
  createPacingHold,
  cancelPacingHold,
  fetchPacingGatingRules,
} from '@src/api/actions';
import { fetchClients } from '@src/api/actions/clients';
import { fetchClientTerritories } from '@src/api/actions/clientTerritories';

const TIER_DEFS = {
  boost: {
    label: 'Push Harder',
    blurb: 'Bid more aggressively in this territory (+25–35%) by shifting spend toward it. Does not increase your monthly budget.',
    icon: TrendingUpIcon,
    color: 'success',
    chipColor: 'success',
    direction: 'boost',
  },
  slow_down: {
    label: 'Slow Down',
    blurb: 'Throttle this territory by ~30–40% (reps OOO, capacity tight).',
    icon: TrendingDownIcon,
    color: 'warning',
    chipColor: 'warning',
    direction: 'throttle',
  },
  minimal_spend: {
    label: 'Minimal Spend',
    blurb: 'Hold spend to a minimum (~75–85% reduction) through the end date.',
    icon: PauseCircleOutlineIcon,
    color: 'error',
    chipColor: 'error',
    direction: 'throttle',
  },
};

const TIER_ORDER = ['boost', 'slow_down', 'minimal_spend'];

const REASON_OPTIONS_THROTTLE = [
  { value: 'calendars_full', label: 'Calendars full' },
  { value: 'team_capacity', label: 'Team at capacity' },
  { value: 'promotion_ended', label: 'Promotion ended' },
  { value: 'lead_quality', label: 'Want to slow lead intake' },
  { value: 'other', label: 'Other' },
];

const REASON_OPTIONS_BOOST = [
  { value: 'under_pacing', label: 'Market is under-pacing — push more' },
  { value: 'sales_capacity', label: 'Reps have extra capacity here' },
  { value: 'territory_priority', label: 'Strategic priority territory' },
  { value: 'lead_quality', label: 'Strong lead quality from this market' },
  { value: 'other', label: 'Other' },
];

const STATUS_COLOR = {
  active: 'warning',
  scheduled: 'info',
  completed: 'default',
  canceled: 'default',
};

function formatDate(s) {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function endOfMonthISO() {
  const now = new Date();
  const eom = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return eom.toISOString().slice(0, 10);
}

function PacingControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClientId = searchParams.get('client_id');
  const clientId = queryClientId ? Number(queryClientId) : null;

  const [clients, setClients] = useState([]);
  const [holds, setHolds] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const [tab, setTab] = useState('active');
  // Deep-link intent (e.g. /pacing-control?client_id=1&action=new from the
  // client dashboard). Captured once on mount; the param itself is stripped
  // immediately (history replace) so a refresh doesn't re-trigger it.
  const [pendingAction, setPendingAction] = useState(() => searchParams.get('action'));

  useEffect(() => {
    if (!searchParams.get('action')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // action=new opens the New Adjustment dialog exactly as the button does —
  // once the button would be enabled (client selected, territories loaded).
  useEffect(() => {
    if (pendingAction !== 'new') return;
    if (loading || !clientId || territories.length === 0) return;
    setPendingAction(null);
    setRequestOpen(true);
  }, [pendingAction, loading, clientId, territories]);

  useEffect(() => {
    Promise.all([fetchClients(), fetchPacingGatingRules()])
      .then(([clientsRes, rulesRes]) => {
        setClients(clientsRes.data?.results || clientsRes.data || []);
        setRules(rulesRes.data || null);
      })
      .catch((err) => setError(err?.message || 'Failed to load metadata.'));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setTerritories([]);
      return;
    }
    fetchClientTerritories(clientId)
      .then((res) => setTerritories(res.data?.results || res.data || []))
      .catch(() => setTerritories([]));
  }, [clientId]);

  const load = useCallback(() => {
    if (!clientId) {
      setHolds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPacingHolds({ client_id: clientId })
      .then((res) => setHolds(res.data?.results || res.data || []))
      .catch((err) => setError(err?.message || 'Failed to load pacing data.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  const handleClientChange = (newClient) => {
    const next = new URLSearchParams(searchParams);
    if (newClient?.id) {
      next.set('client_id', String(newClient.id));
    } else {
      next.delete('client_id');
    }
    setSearchParams(next);
  };

  const active = useMemo(() => holds.filter((h) => h.is_currently_active), [holds]);
  const scheduled = useMemo(() => holds.filter((h) => h.status === 'scheduled' && !h.is_currently_active), [holds]);
  const history = useMemo(() => holds.filter((h) => ['completed', 'canceled'].includes(h.status)), [holds]);

  const tabRows = tab === 'active' ? active : tab === 'scheduled' ? scheduled : history;
  const isHistorical = tab === 'history';

  const handleCancel = (id, { cancelBatch = false, territoryCount = 1 } = {}) => {
    const message = cancelBatch && territoryCount > 1
      ? `Cancel this batch of ${territoryCount} territories? They'll all revert on the next optimizer run.`
      : 'Cancel this adjustment? The optimizer will revert this territory on the next run.';
    if (!window.confirm(message)) return;
    cancelPacingHold(id, { cancelBatch }).then(() => load()).catch((err) => setError(err?.message || 'Cancel failed.'));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pacing &amp; Adjustments</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Push harder or slow down a territory without changing the monthly budget.
            We shift how the optimizer allocates spend; adjustments auto-expire at month-end.
            To raise or lower the budget itself, use the Budget Center.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Autocomplete
            options={clients}
            getOptionLabel={(opt) => opt?.name || ''}
            value={selectedClient}
            onChange={(_, val) => handleClientChange(val)}
            sx={{ flex: 1, maxWidth: 380 }}
            renderInput={(params) => <TextField {...params} label="Client" size="small" />}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          />
          <Button
            variant="contained"
            size="large"
            onClick={() => setRequestOpen(true)}
            disabled={loading || !clientId || territories.length === 0}
            sx={{ minWidth: 180 }}
          >
            New Adjustment
          </Button>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        {!clientId ? (
          <Alert severity="info">Select a client to view and manage their pacing adjustments.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab value="active" label={`Active (${active.length})`} />
              <Tab value="scheduled" label={`Scheduled (${scheduled.length})`} />
              <Tab value="history" label={`History (${history.length})`} />
            </Tabs>
            <CardContent>
              {tabRows.length === 0 ? (
                <EmptyTab tab={tab} onNew={() => setRequestOpen(true)} hasClient={Boolean(clientId)} />
              ) : (
                <HoldList holds={tabRows} onCancel={handleCancel} historical={isHistorical} />
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      <RequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onCreated={() => { setRequestOpen(false); load(); }}
        clientId={clientId}
        territories={territories}
        rules={rules}
      />
    </Box>
  );
}

function EmptyTab({ tab, onNew, hasClient }) {
  const copy = tab === 'active'
    ? { title: 'No adjustments in effect', body: 'The optimizer is running on its default allocation. Submit an adjustment to push more into a territory or slow one down.' }
    : tab === 'scheduled'
      ? { title: 'Nothing scheduled', body: 'You can pre-schedule an adjustment to start on a future date if you know capacity will shift.' }
      : { title: 'No history yet', body: 'Completed and canceled adjustments will show up here.' };
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant="subtitle1" fontWeight={600}>{copy.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 480, mx: 'auto' }}>
        {copy.body}
      </Typography>
      {tab !== 'history' && hasClient && (
        <Button variant="contained" sx={{ mt: 2 }} onClick={onNew}>New Adjustment</Button>
      )}
    </Box>
  );
}

function groupHoldsByBatch(holds) {
  // Holds with the same batch_id collapse into one displayed row labelled
  // with the territory count. Holds with batch_id = null render individually.
  const groups = [];
  const byBatch = new Map();
  for (const h of holds) {
    if (!h.batch_id) {
      groups.push({ key: `single-${h.id}`, lead: h, members: [h] });
      continue;
    }
    if (!byBatch.has(h.batch_id)) {
      const g = { key: `batch-${h.batch_id}`, lead: h, members: [] };
      byBatch.set(h.batch_id, g);
      groups.push(g);
    }
    byBatch.get(h.batch_id).members.push(h);
  }
  return groups;
}

function HoldList({ holds, onCancel, historical = false }) {
  const groups = useMemo(() => groupHoldsByBatch(holds), [holds]);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Territories</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Status</TableCell>
            {!historical && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g) => {
            const h = g.lead;
            const def = TIER_DEFS[h.tier];
            const Icon = def?.icon;
            const isBatch = g.members.length > 1;
            const territoryNames = g.members.map((m) => m.scope_label || 'Territory');
            return (
              <TableRow key={g.key} hover>
                <TableCell>
                  {isBatch ? (
                    <>
                      <Typography variant="body2" fontWeight={500}>
                        {g.members.length} territories
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {territoryNames.join(', ')}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" fontWeight={500}>{h.scope_label || 'Territory'}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {Icon && <Icon fontSize="small" color={def.color} />}
                    <Chip
                      label={def?.label || h.tier_label || h.tier}
                      size="small"
                      color={def?.chipColor || 'default'}
                      variant="outlined"
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(h.start_date)} → {formatDate(h.end_date)}</Typography>
                  <Typography variant="caption" color="text.secondary">{h.duration_days}d</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{(h.reason_code || '').replace(/_/g, ' ') || '—'}</Typography>
                  {h.notes && <Typography variant="caption" color="text.secondary">{h.notes}</Typography>}
                </TableCell>
                <TableCell>
                  <Chip label={h.status} size="small" color={STATUS_COLOR[h.status] || 'default'} />
                </TableCell>
                {!historical && (
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => onCancel(h.id, { cancelBatch: isBatch, territoryCount: g.members.length })}
                    >
                      {isBatch ? 'Cancel batch' : 'Cancel'}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RequestDialog({ open, onClose, onCreated, clientId, territories, rules }) {
  const today = todayISO();
  const monthEnd = endOfMonthISO();
  const [tier, setTier] = useState('boost');
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(monthEnd);
  const [reasonCode, setReasonCode] = useState('under_pacing');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [violations, setViolations] = useState([]);

  const def = TIER_DEFS[tier];
  const reasonOptions = def?.direction === 'boost' ? REASON_OPTIONS_BOOST : REASON_OPTIONS_THROTTLE;

  useEffect(() => {
    if (open) {
      setTier('boost');
      setSelectedTerritories([]);
      setStartDate(today);
      setEndDate(monthEnd);
      setReasonCode('under_pacing');
      setNotes('');
      setValidationError('');
      setViolations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!reasonOptions.find((r) => r.value === reasonCode)) {
      setReasonCode(reasonOptions[0]?.value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const handleSubmit = () => {
    setSubmitting(true);
    setValidationError('');
    setViolations([]);
    const payload = {
      client: clientId,
      scope_type: 'territory',
      scope_ids: selectedTerritories.map((t) => t.id),
      tier,
      start_date: startDate,
      end_date: endDate,
      reason_code: reasonCode,
      notes,
    };
    createPacingHold(payload)
      .then(() => onCreated())
      .catch((err) => {
        const data = err?.response?.data;
        const failedTerritoryId = data?.failed_territory_id;
        const detailMessage = data?.detail || err?.message || 'Request failed.';
        const failedName = failedTerritoryId
          ? territories.find((t) => t.id === failedTerritoryId)?.name
          : null;
        setValidationError(
          failedName ? `${detailMessage} (territory: ${failedName})` : detailMessage,
        );
        setViolations(data?.violations || []);
      })
      .finally(() => setSubmitting(false));
  };

  const submitDisabled = submitting || selectedTerritories.length === 0 || !startDate || !endDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>New Pacing Adjustment</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              What kind of adjustment?
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={tier}
              onChange={(_, v) => v && setTier(v)}
              fullWidth
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  flexDirection: 'column',
                  gap: 0.5,
                  py: 1.25,
                  textTransform: 'none',
                  borderRadius: 1.5,
                },
              }}
            >
              {TIER_ORDER.map((key) => {
                const d = TIER_DEFS[key];
                const Icon = d.icon;
                return (
                  <ToggleButton key={key} value={key} color={d.color}>
                    <Icon fontSize="small" />
                    <Typography variant="caption" fontWeight={600}>{d.label}</Typography>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {def?.blurb}
            </Typography>
          </Box>

          <Box>
            <Autocomplete
              multiple
              size="small"
              options={territories}
              value={selectedTerritories}
              onChange={(_, val) => setSelectedTerritories(val)}
              getOptionLabel={(opt) => opt?.name || ''}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              disabled={territories.length === 0}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={selectedTerritories.length > 1 ? `Territories (${selectedTerritories.length})` : 'Territory'}
                  placeholder={selectedTerritories.length === 0 ? 'Pick one or more…' : ''}
                />
              )}
            />
            {territories.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                No territories configured for this client.
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Pick multiple to apply the same adjustment in one click. They'll group as a single batch you can cancel together.
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth size="small" label="Start" type="date"
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today, max: monthEnd }}
            />
            <TextField
              fullWidth size="small" label="End" type="date"
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today, max: monthEnd }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Default is through month-end. Pick an earlier end date for short-term holds.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Reason</InputLabel>
            <Select value={reasonCode} label="Reason" onChange={(e) => setReasonCode(e.target.value)}>
              {reasonOptions.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" multiline rows={2} label="Notes (optional)"
            value={notes} onChange={(e) => setNotes(e.target.value)}
          />

          {def?.direction === 'boost' && territories.length === 1 && (
            <Alert severity="warning" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              This account has a single territory, so there are no other markets to shift spend from.
              If campaigns are already pacing to budget, Push Harder won't add lead volume — increasing
              the monthly budget in the Budget Center is the lever that will.
            </Alert>
          )}

          <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
            <strong>How this works:</strong> we send a stronger bidding signal to push more spend into (or pull back from) the territories you pick. The optimizer updates within ~15 minutes. We can't guarantee the exact percentage change in delivered spend — Meta's algorithm decides where dollars actually land — but your request directly shifts how aggressively we bid in that market.
            <br />
            <strong>Your monthly budget stays the same</strong> — this only changes where it's spent. To increase or decrease the budget itself, use the Budget Center.
          </Alert>

          {validationError && (
            <Alert severity="error">
              {validationError}
              {violations.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption">Violations:</Typography>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {violations.map((v) => <li key={v}><Typography variant="caption">{v.replace(/_/g, ' ')}</Typography></li>)}
                  </ul>
                </Box>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
          {submitting ? <CircularProgress size={18} /> : 'Submit Adjustment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PacingControlPage;
